import { useState } from 'react';
import type { AnalysisResults } from '../../services/api/novel-analysis';

type ParseFn = (str: string | null) => unknown;

export function CharactersTab({ characters, parseJson }: { characters: AnalysisResults['characters']; parseJson: ParseFn }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      {characters.map((char) => {
        const isExpanded = expandedId === char.id;
        const appearance = parseJson(char.appearance) as { summary?: string; distinctive_features?: string[]; height?: string; build?: string } | null;
        const personality = parseJson(char.personality) as { summary?: string; core_traits?: string[]; speech_style?: string } | null;
        const currentState = parseJson(char.current_state) as { physical?: string; mental?: string; location?: string } | null;
        const charmTendencies = parseJson(char.charm_tendencies) as { primary?: string; secondary?: string; triggers?: string[] } | null;
        const dialogueFeatures = parseJson(char.dialogue_features) as { self_reference?: string; modes?: Array<{ mode?: string; description?: string }> } | null;
        const writingNotes = parseJson(char.writing_notes) as { do?: string[]; avoid?: string[] } | null;

        return (
          <div key={char.id} className={`border rounded-lg transition-all ${isExpanded ? 'md:col-span-2' : ''}`}>
            {/* Header - always visible */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : char.id)}
              className="w-full text-left p-4"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-sm shrink-0">
                  {char.name[0]}
                </div>
                <div className="min-w-0">
                  <h3 className="font-medium">{char.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {char.gender && <span>{char.gender === 'male' ? '男' : char.gender === 'female' ? '女' : '未知'}</span>}
                    {char.age && <span>{char.age}</span>}
                    {char.occupation && <span>· {char.occupation}</span>}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  {char.is_major && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">主要角色</span>}
                  <span className="text-gray-400 text-sm">{isExpanded ? '收起' : '展開'}</span>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1 ml-10">
                {(parseJson(char.aliases) as string[] || []).length > 0 && (
                  <span>別名：{(parseJson(char.aliases) as string[]).join(', ')} · </span>
                )}
                出現：第 {char.first_appearance_chapter || 1} ~ {char.last_seen_chapter || 1} 章（共 {char.chapter_count} 章）
              </div>
            </button>

            {/* Expanded details */}
            {isExpanded && (
              <div className="px-4 pb-4 space-y-3 border-t pt-3">
                {/* Appearance */}
                {appearance && (
                  <Section title="外貌">
                    {appearance.summary && <p className="text-sm text-gray-700">{appearance.summary}</p>}
                    <div className="flex gap-2 flex-wrap mt-1">
                      {appearance.height && <Tag label="身高" value={appearance.height} />}
                      {appearance.build && <Tag label="體型" value={appearance.build} />}
                    </div>
                    {appearance.distinctive_features?.length ? (
                      <div className="flex gap-1 flex-wrap mt-1">
                        {appearance.distinctive_features.map((f, i) => (
                          <span key={i} className="px-2 py-0.5 bg-pink-50 text-pink-700 rounded text-xs">{f}</span>
                        ))}
                      </div>
                    ) : null}
                  </Section>
                )}

                {/* Personality */}
                {personality && (
                  <Section title="性格">
                    {personality.summary && <p className="text-sm text-gray-700">{personality.summary}</p>}
                    {personality.core_traits?.length ? (
                      <div className="flex gap-1 flex-wrap mt-1">
                        {personality.core_traits.map((t, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{t}</span>
                        ))}
                      </div>
                    ) : null}
                    {personality.speech_style && <p className="text-xs text-gray-500 mt-1">說話風格：{personality.speech_style}</p>}
                  </Section>
                )}

                {/* Psychology */}
                {char.psychology && (
                  <Section title="心理描述">
                    <p className="text-sm text-gray-700">{char.psychology}</p>
                  </Section>
                )}

                {/* Current State */}
                {currentState && (
                  <Section title="當前狀態">
                    <div className="grid grid-cols-3 gap-2">
                      {currentState.physical && <StateCard label="身體" value={currentState.physical} />}
                      {currentState.mental && <StateCard label="心理" value={currentState.mental} />}
                      {currentState.location && <StateCard label="位置" value={currentState.location} />}
                    </div>
                  </Section>
                )}

                {/* Charm Tendencies */}
                {charmTendencies && (
                  <Section title="風情傾向">
                    <div className="flex gap-2 flex-wrap">
                      {charmTendencies.primary && <Tag label="主要" value={charmTendencies.primary} color="pink" />}
                      {charmTendencies.secondary && <Tag label="次要" value={charmTendencies.secondary} color="purple" />}
                    </div>
                    {charmTendencies.triggers?.length ? (
                      <div className="mt-1">
                        <span className="text-xs text-gray-500">觸發條件：</span>
                        {charmTendencies.triggers.map((t, i) => (
                          <span key={i} className="text-xs text-gray-600 ml-1">{t}{i < charmTendencies.triggers!.length - 1 ? '、' : ''}</span>
                        ))}
                      </div>
                    ) : null}
                  </Section>
                )}

                {/* Dialogue Features */}
                {dialogueFeatures && (
                  <Section title="對話特徵">
                    {dialogueFeatures.self_reference && <p className="text-sm text-gray-700">自稱：{dialogueFeatures.self_reference}</p>}
                    {dialogueFeatures.modes?.map((m, i) => (
                      <div key={i} className="text-sm text-gray-600 pl-3 border-l-2 border-gray-200 mt-1">
                        <span className="font-medium">{m.mode}</span>
                        {m.description && <span className="text-gray-500"> — {m.description}</span>}
                      </div>
                    ))}
                  </Section>
                )}

                {/* Writing Notes */}
                {writingNotes && (
                  <Section title="寫作注意事項">
                    {writingNotes.do?.length ? (
                      <div className="mb-1">
                        <span className="text-xs font-medium text-green-600">應該：</span>
                        {writingNotes.do.map((d, i) => (
                          <div key={i} className="text-sm text-gray-600 pl-3">✓ {d}</div>
                        ))}
                      </div>
                    ) : null}
                    {writingNotes.avoid?.length ? (
                      <div>
                        <span className="text-xs font-medium text-red-600">避免：</span>
                        {writingNotes.avoid.map((a, i) => (
                          <div key={i} className="text-sm text-gray-600 pl-3">✗ {a}</div>
                        ))}
                      </div>
                    ) : null}
                  </Section>
                )}
              </div>
            )}
          </div>
        );
      })}
      {characters.length === 0 && <p className="text-gray-500 text-sm col-span-2">尚無角色資料</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-1">{title}</h4>
      {children}
    </div>
  );
}

function Tag({ label, value, color = 'gray' }: { label: string; value: string; color?: string }) {
  const colors: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700',
    pink: 'bg-pink-50 text-pink-700',
    purple: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[color] || colors.gray}`}>
      {label}：{value}
    </span>
  );
}

function StateCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded p-2">
      <span className="text-xs text-gray-500 block">{label}</span>
      <span className="text-sm text-gray-700">{value}</span>
    </div>
  );
}
